import { CssProperties } from "src/models/css-property";
import { ElementStyleService } from "../../../services/element-style.service";
import { StringUtil } from "../../../utils/string-util";
import { IStoreCheckoutWidgetStyle } from "@pageBuilder/typings";
import { AlphaChannelService } from "src/services/alpha-channel.service";

export class StoreCheckoutWidgetStyleRenderer {

    private readonly alphaChannelService: AlphaChannelService;

    constructor(private readonly styleService: ElementStyleService
    ) {
        this.alphaChannelService = new AlphaChannelService();
    }

    public render(element: HTMLElement, style: IStoreCheckoutWidgetStyle): void {
        this.renderCommonStyles(element, style);
        this.renderOrderSummaryStyles(element, style);
        this.renderFormStyles(element, style);
        this.renderButtonStyles(element, style);
    }

    private renderCommonStyles(element: HTMLElement, style: IStoreCheckoutWidgetStyle): void {
        const styles = new CssProperties();

        styles.add("--container-background", style.container.backgroundColor);
        if (style.container.borderRadius) {
            styles.tryAdd("--container-border-top-left-radius", `${style.container.borderRadius.topLeftPx}px`);
            styles.tryAdd("--container-border-top-right-radius", `${style.container.borderRadius.topRightPx}px`);
            styles.tryAdd("--container-border-bottom-left-radius", `${style.container.borderRadius.bottomLeftPx}px`);
            styles.tryAdd("--container-border-bottom-right-radius", `${style.container.borderRadius.bottomRightPx}px`);
        }

        if (style.container?.font.size) {
            styles.tryAdd("--font-size", String(StringUtil.getNumberFromStr(style.container.font.size)));
        }

        styles.tryAdd("--font-family", style.container.font?.family ?? "");
        styles.tryAdd("--color", style.container.font.color ?? "");
        styles.tryAdd("--error-color", style.container.errorMessageColor ?? "");

        styles.tryAdd("--button-background-color", style.button.backgroundColor?.colorDefault ?? "");
        styles.tryAdd("--button-border-color", style.button.borderColor?.colorDefault ?? "");
        styles.tryAdd("--button-color", style.button.color?.colorDefault ?? "");

        this.styleService.setStyle(element, `#${element.id} .widget-content`, styles.data);
    }

    private renderOrderSummaryStyles(element: HTMLElement, style: IStoreCheckoutWidgetStyle): void {
        const styles = new CssProperties();

        styles.add("--order-summary-container-background", style.summaryContainer.backgroundColor);
        styles.tryAdd("--order-summary-font-family", style.container.font?.family ?? "");
        styles.tryAdd("--order-summary-color", style.summaryContainer.font.color ?? "");

        if (style.summaryContainer.borderRadius) {
            styles.tryAdd("--order-summary-container-border-top-left-radius", `${style.summaryContainer.borderRadius.topLeftPx}px`);
            styles.tryAdd("--order-summary-container-border-top-right-radius", `${style.summaryContainer.borderRadius.topRightPx}px`);
            styles.tryAdd("--order-summary-container-border-bottom-left-radius", `${style.summaryContainer.borderRadius.bottomLeftPx}px`);
            styles.tryAdd("--order-summary-container-border-bottom-right-radius", `${style.summaryContainer.borderRadius.bottomRightPx}px`);
        }

        if (style.summaryContainer.dividerColor) {
            styles.tryAdd("--order-summary-divider-color", style.summaryContainer.dividerColor ?? "");
        }

        this.styleService.setStyle(element, `#${element.id} .order-summary-wrapper`, styles.data);
    }

    private renderFormStyles(element: HTMLElement, style: IStoreCheckoutWidgetStyle): void {
        const formStyles = new CssProperties();
        formStyles.add("--input-border-color", style.container.inputBorderColor);

        if (style.container.linkColor.colorDefault) {
            formStyles.add("--link-default-color", style.container.linkColor.colorDefault);
        }

        if (style.container.linkColor.colorHovered) {
            formStyles.add("--link-hover-color", style.container.linkColor.colorHovered);
        }

        this.styleService.setStyle(element, `#${element.id} .checkout-forms-wrapper`, formStyles.data);
    }

    private renderButtonStyles(element: HTMLElement, style: IStoreCheckoutWidgetStyle): void {
        const cssProperties = new CssProperties();

        if (style.button.font?.size) {
            cssProperties.tryAdd("--button-font-size", String(StringUtil.getNumberFromStr(style.button.font.size)));
        }
        cssProperties.tryAdd("--button-font-family", style.button.font?.family ?? "");
        cssProperties.tryAdd("--button-font-height", style.button.font?.lineHeight ?? "");
        cssProperties.tryAdd("--button-font-weight", style.button.font?.weight ?? "");
        cssProperties.tryAdd("--button-color", style.button.color?.colorDefault ?? "");
        cssProperties.tryAdd("--button-hovered-color", style.button.color?.colorHovered ?? "");
        cssProperties.tryAdd("--button-border-color", style.button.borderColor?.colorDefault ?? "");
        cssProperties.tryAdd("--button-hovered-border-color", style.button.borderColor?.colorHovered ?? "");

        cssProperties.tryAdd("--button-border-width", style.button.thickness);
        if (style.button.borderRadius) {
            cssProperties.tryAdd("--button-border-top-left-radius", `${style.button.borderRadius.topLeftPx}px`);
            cssProperties.tryAdd("--button-border-top-right-radius", `${style.button.borderRadius.topRightPx}px`);
            cssProperties.tryAdd("--button-border-bottom-left-radius", `${style.button.borderRadius.bottomLeftPx}px`);
            cssProperties.tryAdd("--button-border-bottom-right-radius", `${style.button.borderRadius.bottomRightPx}px`);
        }

        cssProperties.tryAdd("--button-background-color", style.button.backgroundColor?.colorDefault ?? "");
        cssProperties.tryAdd("--button-hovered-background-color", style.button.backgroundColor?.colorHovered ?? "");

        this.styleService.setStyle(element, `#${element.id} .button`, cssProperties.data);
        this.styleService.setStyle(element, `#${element.id} .primary-button`, cssProperties.data);
    }

}
